import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Settings, User, KeyRound, Palette, LogOut, Eye, EyeOff, Loader2 } from "lucide-react";
import PageHeader from "../components/common/PageHeader";
import useAlert from "../components/hook/useAlert";
import callApi from "../helpers/networkHelper";
import API_CONFIG from "../config/apiConstant";
import { selectUser, logout } from "../store/slices/authSlice";
import { useTheme } from "../contexts/ThemeContext";

const getReadableErrorMessage = (error) => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return "Something went wrong. Please try again.";
};

const Card = ({ icon, title, description, children }) => (
    <section
        className="rounded-xl p-5 sm:p-6"
        style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)" }}
    >
        <div className="flex items-start gap-3 mb-5">
            <div
                className="rounded-lg p-2 shrink-0"
                style={{ background: "var(--brand-light)", color: "var(--brand)" }}
            >
                {icon}
            </div>
            <div className="min-w-0">
                <h2 className="text-base font-semibold" style={{ color: "var(--main-text)" }}>
                    {title}
                </h2>
                {description && (
                    <p className="text-sm mt-0.5" style={{ color: "var(--main-text-muted)" }}>
                        {description}
                    </p>
                )}
            </div>
        </div>
        {children}
    </section>
);

const Field = ({ label, value }) => (
    <div>
        <p className="text-xs uppercase tracking-wide" style={{ color: "var(--main-text-muted)" }}>
            {label}
        </p>
        <p className="text-sm font-medium mt-1 break-words" style={{ color: "var(--main-text)" }}>
            {value || "—"}
        </p>
    </div>
);

const PasswordInput = ({ id, label, value, error, onChange, disabled }) => {
    const [visible, setVisible] = useState(false);

    return (
        <div>
            <label
                htmlFor={id}
                className="block text-sm font-medium mb-2"
                style={{ color: "var(--main-text)" }}
            >
                {label} *
            </label>
            <div className="relative">
                <input
                    id={id}
                    name={id}
                    type={visible ? "text" : "password"}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    autoComplete={id === "oldPassword" ? "current-password" : "new-password"}
                    className={`w-full px-4 pr-11 py-3 h-11 border rounded-lg bg-input text-foreground
            placeholder:text-muted-foreground focus:ring-brand focus:border-transparent
            transition duration-200 ${error ? "border-destructive" : "border-border"}`}
                    placeholder={label}
                />
                <button
                    type="button"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: "var(--main-text-muted)" }}
                >
                    {visible ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
            </div>
            {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
        </div>
    );
};

const SettingsPage = () => {
    const user = useSelector(selectUser);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const { showSuccess, showError } = useAlert();

    const [form, setForm] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // Mirrors the backend Joi schema so a rejected password is caught before
    // the round trip rather than coming back as a server error string.
    const validate = () => {
        const next = {};

        if (!form.oldPassword) {
            next.oldPassword = "Current password is required";
        }

        if (!form.newPassword) {
            next.newPassword = "New password is required";
        } else if (form.newPassword.length < 8) {
            next.newPassword = "New password must be at least 8 characters long";
        } else if (
            !/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/.test(form.newPassword)
        ) {
            next.newPassword =
                "Password must include at least one letter, one number, and one special character (@$!%*?&)";
        } else if (form.newPassword === form.oldPassword) {
            next.newPassword = "New password cannot be the same as the current password";
        }

        if (form.confirmPassword !== form.newPassword) {
            next.confirmPassword = "Passwords do not match";
        }

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            await callApi(
                API_CONFIG.AUTH.CHANGE_PASSWORD.METHOD,
                API_CONFIG.AUTH.CHANGE_PASSWORD.ENDPOINT,
                {
                    email: user?.email,
                    oldPassword: form.oldPassword,
                    newPassword: form.newPassword,
                }
            );

            setForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
            showSuccess("Password changed successfully. Please sign in again.");

            // The old credentials are gone; keeping the session alive invites
            // the next person at the keyboard to keep using it.
            dispatch(logout());
            navigate("/login");
        } catch (err) {
            showError(getReadableErrorMessage(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        showSuccess("Logged out successfully!");
        navigate("/login");
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <PageHeader
                icon={<Settings size={28} />}
                title="Settings"
                subtitle="Manage your account, password and appearance preferences."
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl">
                <Card icon={<User size={20} />} title="Profile" description="Your account details.">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Username" value={user?.userName} />
                        <Field label="Email" value={user?.email} />
                        {user?.role && <Field label="Role" value={user.role} />}
                        {user?.mobileNumber && <Field label="Mobile" value={user.mobileNumber} />}
                    </div>
                </Card>

                <Card
                    icon={<Palette size={20} />}
                    title="Appearance"
                    description="Theme is remembered on this browser."
                >
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <p className="text-sm font-medium" style={{ color: "var(--main-text)" }}>
                                {theme === "dark" ? "Dark mode" : "Light mode"}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: "var(--main-text-muted)" }}>
                                Switch between the light and dark colour scheme.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={toggleTheme}
                            className="rounded-lg px-4 py-2 text-sm font-medium transition-colors shrink-0"
                            style={{
                                background: "var(--sidebar-hover)",
                                color: "var(--main-text)",
                                border: "1px solid var(--card-border)",
                            }}
                        >
                            Switch to {theme === "dark" ? "light" : "dark"}
                        </button>
                    </div>
                </Card>

                <Card
                    icon={<KeyRound size={20} />}
                    title="Change password"
                    description="You will be signed out once the password changes."
                >
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <PasswordInput
                            id="oldPassword"
                            label="Current password"
                            value={form.oldPassword}
                            error={errors.oldPassword}
                            onChange={handleChange}
                            disabled={isSubmitting}
                        />
                        <PasswordInput
                            id="newPassword"
                            label="New password"
                            value={form.newPassword}
                            error={errors.newPassword}
                            onChange={handleChange}
                            disabled={isSubmitting}
                        />
                        <PasswordInput
                            id="confirmPassword"
                            label="Confirm new password"
                            value={form.confirmPassword}
                            error={errors.confirmPassword}
                            onChange={handleChange}
                            disabled={isSubmitting}
                        />

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg
                text-sm font-medium transition duration-200
                ${isSubmitting ? "cursor-not-allowed opacity-70" : ""}`}
                            style={{ background: "var(--brand)", color: "#fff" }}
                        >
                            {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                            {isSubmitting ? "Updating…" : "Update password"}
                        </button>
                    </form>
                </Card>

                <Card
                    icon={<LogOut size={20} />}
                    title="Session"
                    description="Sign out of the license portal on this device."
                >
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                        style={{
                            background: "transparent",
                            color: "var(--danger)",
                            border: "1px solid var(--danger)",
                        }}
                    >
                        Logout
                    </button>
                </Card>
            </div>
        </div>
    );
};

export default SettingsPage;
